import { Link } from "react-router-dom";
import { Pct } from "@/components/layernav/primitives";
import { track } from "@/components/layernav/navConfig";
import { timeAgo } from "@/lib/api";
import { SectorMarketSnapshot, SectorLayerIndex } from "./SectorMarketPanel";

export const sectorTrack = (module: string, target?: string, sector: string = "crypto") =>
  track(`sector_${module}`, { sector, target });

export const fmtBig = (n?: number | null) => {
  if (n == null) return "—";
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${Math.round(n).toLocaleString()}`;
};

const LiveChip = ({ updatedAt }: { updatedAt?: string | null }) => {
  if (!updatedAt) return null;
  const fresh = Date.now() - new Date(updatedAt).getTime() < 15 * 60 * 1000;
  return fresh ? (
    <span data-testid="sector-pulse-live" className="inline-flex items-center gap-1.5 rounded-full border border-[#34D399]/40 bg-[#34D399]/10 px-2 py-0.5 font-mono text-[9px] font-bold tracking-[0.18em] text-[#34D399]">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#34D399]" />
      LIVE
    </span>
  ) : (
    <span data-testid="sector-pulse-updated" className="font-mono text-[9px] uppercase tracking-widest text-gray-500">
      Updated {timeAgo(updatedAt)}
    </span>
  );
};

const Cell = ({ label, value, delta, testid }: {
  label: string; value: string; delta?: number | null; testid: string;
}) => (
  <div className="px-4 py-3 sm:px-5 min-w-0" data-testid={testid}>
    <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-gray-500 whitespace-nowrap">{label}</p>
    <div className="flex items-baseline gap-2 mt-1">
      <span className="font-mono text-lg sm:text-xl font-bold text-white">{value}</span>
      {delta != null && <Pct v={delta} className="!text-[11px]" />}
    </div>
  </div>
);

export const SectorHero = ({ data }: { data: any }) => {
  const pulse = data?.pulse || {};
  const trending: any[] = data?.trending || [];
  return (
    <section className="relative overflow-hidden border-b border-white/[0.06]" data-testid="crypto-sector-hero">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <img src="/hero/crypto-sector.jpg" alt="" fetchPriority="high"
          className="w-full h-full object-cover object-[62%_58%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000] via-[#000000]/60 to-[#000000]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-[#000000]/45" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-8">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,1fr)_440px] gap-8 xl:gap-12 items-start">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#00E5FF]">Sector</p>
            <h1 data-testid="crypto-masthead"
              className="font-heading font-black text-white text-5xl sm:text-6xl xl:text-7xl tracking-tight mt-1.5">
              CRYPTO
            </h1>
            <p className="text-gray-300 text-[15px] sm:text-base mt-3 max-w-xl leading-relaxed">
              {data?.sector?.description || "Digital assets, markets, infrastructure and the forces shaping the crypto economy."}
            </p>

            <div className="mt-6">
              <div className="flex items-center gap-3">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#A78BFA]">Sector Pulse</p>
                <LiveChip updatedAt={pulse.updated_at} />
              </div>
              <div data-testid="sector-pulse"
                className="mt-3 inline-grid grid-cols-2 sm:grid-cols-4 rounded-xl border border-white/[0.09] bg-black/50 backdrop-blur-md divide-x divide-y sm:divide-y-0 divide-white/[0.06] overflow-hidden">
                <Cell testid="pulse-market-cap" label="Market Cap"
                  value={fmtBig(pulse.market_cap_usd)} delta={pulse.market_cap_change_24h_pct} />
                <Cell testid="pulse-volume" label="24H Volume" value={fmtBig(pulse.volume_24h_usd)} />
                <Cell testid="pulse-dominance" label="BTC Dominance"
                  value={pulse.btc_dominance != null ? `${Number(pulse.btc_dominance).toFixed(1)}%` : "—"} />
                <Cell testid="pulse-articles" label="Crypto Stories · 24H"
                  value={pulse.articles_24h != null ? String(pulse.articles_24h) : "—"} />
              </div>
            </div>

            {trending.length > 0 && (
              <div className="mt-5" data-testid="sector-trending">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2.5"
                  title="Topic activity across NewsLayer crypto coverage, last 48 hours">
                  Top Trending <span className="text-gray-600">· 48H</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {trending.map((t) => (
                    <Link key={t.topic} to={`/search?q=${encodeURIComponent(t.label)}`}
                      data-testid={`trending-chip-${t.topic}`}
                      onClick={() => sectorTrack("trending_topic_click", t.topic)}
                      className="group inline-flex items-center gap-2 rounded-full border border-white/[0.14] bg-white/[0.05] backdrop-blur-sm px-3.5 py-1.5 text-[13px] font-semibold text-gray-200 hover:border-[#00E5FF]/60 hover:text-white transition-colors">
                      {t.label}
                      <span className="font-mono text-[10px] text-gray-500 group-hover:text-[#00E5FF] transition-colors">{t.count}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="hidden lg:flex flex-col gap-4">
            <SectorMarketSnapshot />
            <SectorLayerIndex />
          </div>
        </div>
      </div>
    </section>
  );
};
